import React from 'react';
import {
  Box,
  Flex,
  Text,
  Select,
  Checkbox,
  Spinner,
  Divider,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import xhr from 'xhr';

// Tipos de programa que pueden recibir una clase compartida.
const PROGRAM_TYPES = [
  { type: 'grade', endpoint: 'grades', label: 'Grados' },
  { type: 'technical_career', endpoint: 'technical-careers', label: 'Carreras técnicas' },
];

const keyOf = (type, id) => `${type}:${id}`;

/**
 * Selector para clases en vivo COMPARTIDAS entre varios programas (grados o
 * carreras técnicas).
 *
 * Deja elegir un programa, y al elegirlo muestra sus GRUPOS y MATERIAS para
 * marcar cuáles verán esta misma clase (en vivo y grabada). Acumula la
 * selección por programa y notifica al padre el conjunto plano de ids:
 *
 *   onChange({ other_subjects: number[], other_class_groups: number[], valid: boolean })
 *
 * `valid` es false si algún programa agregado no tiene materias: sin materia
 * la grabación no le aparece a nadie de ese programa.
 *
 * `initialSubjects` / `initialGroups` (ids) precargan la selección al editar.
 */
export default function SharedClassPicker({
  currentProgramType,
  currentProgramId,
  initialSubjects = [],
  initialGroups = [],
  onChange,
}) {
  const [programs, setPrograms] = React.useState([]);
  const [loadingPrograms, setLoadingPrograms] = React.useState(true);
  const [activeKey, setActiveKey] = React.useState('');

  // Cachés por programa
  const [groupsByProgram, setGroupsByProgram] = React.useState({});
  const [subjectsByProgram, setSubjectsByProgram] = React.useState({});
  const [loadingLists, setLoadingLists] = React.useState(false);

  // Selección: { [programKey]: { name, groups: number[], subjects: number[] } }
  const [picks, setPicks] = React.useState({});

  // Programas activos de todos los tipos (excluyendo el programa principal)
  React.useEffect(() => {
    (async () => {
      try {
        const lists = await Promise.all(
          PROGRAM_TYPES.map(async (pt) => {
            const res = await xhr(
              `/${pt.endpoint}?filters[active][$eq]=true&fields[0]=id&fields[1]=name&pagination[limit]=-1&sort[0]=name:asc`
            );
            return (res.data?.data || [])
              .filter((el) => !(pt.type === currentProgramType && String(el.id) === String(currentProgramId)))
              .map((el) => ({ key: keyOf(pt.type, el.id), type: pt.type, id: el.id, name: el.name }));
          })
        );
        setPrograms(lists.flat());
      } catch (error) {
        setPrograms([]);
      } finally {
        setLoadingPrograms(false);
      }
    })();
  }, [currentProgramType, currentProgramId]);

  // Precarga al editar: agrupa materias y grupos guardados por su programa
  React.useEffect(() => {
    if (!initialSubjects.length && !initialGroups.length) return;
    (async () => {
      try {
        const ids = (arr) => arr.map((id, i) => `filters[id][$in][${i}]=${id}`).join('&');
        const populate = 'populate[grade][fields][0]=name&populate[technical_career][fields][0]=name';
        const [subjectsRes, groupsRes] = await Promise.all([
          initialSubjects.length ? xhr(`/subjects?${ids(initialSubjects)}&${populate}&pagination[limit]=-1`) : null,
          initialGroups.length ? xhr(`/class-groups?${ids(initialGroups)}&${populate}&pagination[limit]=-1`) : null,
        ]);
        const next = {};
        const add = (el, kind) => {
          const pt = PROGRAM_TYPES.find((p) => el[p.type]?.id);
          if (!pt) return;
          const key = keyOf(pt.type, el[pt.type].id);
          next[key] = next[key] || { name: el[pt.type].name, groups: [], subjects: [] };
          next[key][kind].push(el.id);
        };
        (subjectsRes?.data?.data || []).forEach((el) => add(el, 'subjects'));
        (groupsRes?.data?.data || []).forEach((el) => add(el, 'groups'));
        setPicks(next);
      } catch (error) {
        setPicks({});
      }
    })();
    // Solo al montar: la selección inicial no cambia mientras se edita
  }, []);

  // Notificar al padre el conjunto plano (sin duplicados)
  React.useEffect(() => {
    const subjects = new Set();
    const groups = new Set();
    Object.values(picks).forEach((p) => {
      (p.subjects || []).forEach((id) => subjects.add(id));
      (p.groups || []).forEach((id) => groups.add(id));
    });
    onChange({
      other_subjects: [...subjects],
      other_class_groups: [...groups],
      valid: Object.values(picks).every((p) => p.subjects?.length > 0),
    });
  }, [picks]);

  const loadProgramLists = async (key) => {
    if (groupsByProgram[key] && subjectsByProgram[key]) return;
    const program = programs.find((p) => p.key === key);
    if (!program) return;
    setLoadingLists(true);
    try {
      const [groupsRes, subjectsRes] = await Promise.all([
        xhr(
          `/class-groups?filters[${program.type}][id][$eq]=${program.id}&filters[active][$eq]=true&fields[0]=id&fields[1]=name&pagination[limit]=-1&sort[0]=name:asc`
        ),
        xhr(
          `/subjects?filters[${program.type}][id][$eq]=${program.id}&fields[0]=id&fields[1]=name&fields[2]=period&pagination[limit]=-1&sort[0]=name:asc`
        ),
      ]);
      setGroupsByProgram((prev) => ({
        ...prev,
        [key]: (groupsRes.data?.data || []).map((el) => ({ id: el.id, name: el.name })),
      }));
      setSubjectsByProgram((prev) => ({
        ...prev,
        [key]: (subjectsRes.data?.data || []).map((el) => ({
          id: el.id,
          name: el.period ? `${el.name} (${el.period})` : el.name,
        })),
      }));
    } catch (error) {
      setGroupsByProgram((prev) => ({ ...prev, [key]: [] }));
      setSubjectsByProgram((prev) => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingLists(false);
    }
  };

  const handleProgramChange = async (event) => {
    const key = event.target.value;
    setActiveKey(key);
    if (key) await loadProgramLists(key);
  };

  const programName = (key) => programs.find((p) => p.key === key)?.name || picks[key]?.name || key;

  const toggleItem = (key, kind, id, checked) => {
    setPicks((prev) => {
      const current = prev[key] || { name: programName(key), groups: [], subjects: [] };
      const list = new Set(current[kind]);
      if (checked) list.add(id);
      else list.delete(id);
      const next = { ...current, name: programName(key), [kind]: [...list] };
      // Si el programa queda sin nada seleccionado, lo quitamos del resumen
      if (!next.groups.length && !next.subjects.length) {
        const { [key]: _omit, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: next };
    });
  };

  const removeProgram = (key) => {
    setPicks((prev) => {
      const { [key]: _omit, ...rest } = prev;
      return rest;
    });
    if (activeKey === key) setActiveKey('');
  };

  const activeGroups = groupsByProgram[activeKey] || [];
  const activeSubjects = subjectsByProgram[activeKey] || [];
  const activePick = picks[activeKey] || { groups: [], subjects: [] };

  const totalSubjects = Object.values(picks).reduce((acc, p) => acc + (p.subjects?.length || 0), 0);
  const totalGroups = Object.values(picks).reduce((acc, p) => acc + (p.groups?.length || 0), 0);

  return (
    <Box border="1px solid #e2e8f0" borderRadius="8px" p="12px" bg="#fafaff">
      <Text fontSize="13px" color="gray.600" mb="8px">
        Elige otro programa y marca las <b>materias</b> que verán esta misma clase (en vivo y grabada). Los{' '}
        <b>grupos</b> son opcionales: si marcas alguno, solo esos grupos la verán. Puedes agregar varios programas.
      </Text>

      {loadingPrograms ? (
        <Spinner size="sm" />
      ) : (
        <Select placeholder="Selecciona un programa" value={activeKey} onChange={handleProgramChange} bg="white" size="sm">
          {PROGRAM_TYPES.map((pt) => (
            <optgroup key={pt.type} label={pt.label}>
              {programs
                .filter((p) => p.type === pt.type)
                .map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.name}
                  </option>
                ))}
            </optgroup>
          ))}
        </Select>
      )}

      {activeKey && (
        <Box mt="12px">
          {loadingLists ? (
            <Flex justify="center" py="10px">
              <Spinner size="sm" />
            </Flex>
          ) : (
            <Flex gap="16px" direction={{ base: 'column', md: 'row' }}>
              <Box flex="1">
                <Text fontSize="12px" fontWeight="600" color="gray.500" mb="6px" textTransform="uppercase">
                  Materias
                </Text>
                <Box maxH="160px" overflowY="auto" pr="4px">
                  {activeSubjects.length === 0 && (
                    <Text fontSize="13px" color="gray.400">
                      Sin materias
                    </Text>
                  )}
                  {activeSubjects.map((s) => (
                    <Box key={s.id} mb="4px">
                      <Checkbox
                        size="sm"
                        isChecked={activePick.subjects?.includes(s.id)}
                        onChange={(e) => toggleItem(activeKey, 'subjects', s.id, e.target.checked)}
                      >
                        {s.name}
                      </Checkbox>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box flex="1">
                <Text fontSize="12px" fontWeight="600" color="gray.500" mb="6px" textTransform="uppercase">
                  Grupos (opcional)
                </Text>
                <Box maxH="160px" overflowY="auto" pr="4px">
                  {activeGroups.length === 0 && (
                    <Text fontSize="13px" color="gray.400">
                      Sin grupos
                    </Text>
                  )}
                  {activeGroups.map((g) => (
                    <Box key={g.id} mb="4px">
                      <Checkbox
                        size="sm"
                        isChecked={activePick.groups?.includes(g.id)}
                        onChange={(e) => toggleItem(activeKey, 'groups', g.id, e.target.checked)}
                      >
                        {g.name}
                      </Checkbox>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Flex>
          )}
        </Box>
      )}

      {Object.keys(picks).length > 0 && (
        <>
          <Divider my="12px" />
          <Text fontSize="12px" fontWeight="600" color="gray.500" mb="6px">
            Programas agregados ({totalSubjects} materias · {totalGroups} grupos)
          </Text>
          <Wrap>
            {Object.entries(picks).map(([key, p]) => {
              const missingSubject = !p.subjects?.length;
              return (
                <WrapItem key={key}>
                  <Tag
                    colorScheme={missingSubject ? 'red' : 'purple'}
                    borderRadius="full"
                    cursor="pointer"
                    onClick={() => handleProgramChange({ target: { value: key } })}
                  >
                    <TagLabel>
                      {p.name} ({p.subjects?.length || 0}m · {p.groups?.length || 0}g)
                      {missingSubject ? ' · falta materia' : ''}
                    </TagLabel>
                    <TagCloseButton
                      onClick={(e) => {
                        e.stopPropagation();
                        removeProgram(key);
                      }}
                    />
                  </Tag>
                </WrapItem>
              );
            })}
          </Wrap>
        </>
      )}
    </Box>
  );
}
